import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Heart,
  Users,
  Eye,
  EyeOff,
  MapPin,
  Sparkles,
  Clock,
  Shield,
  Info,
  Loader2,
  ChevronRight,
  Bluetooth,
  Radio,
  Lock
} from "lucide-react";

const CrushPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [crossings, setCrossings] = useState([]);
  const [crushes, setCrushes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [config, setConfig] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crush/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
      setIsVisible(res.data.is_visible);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  const fetchCrossings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crush/crossings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrossings(res.data.crossings || []);
    } catch (error) {
      console.error("Error fetching crossings:", error);
    }
  }, [token]);

  const fetchCrushes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crush/crushes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrushes(res.data.crushes || []);
    } catch (error) {
      console.error("Error fetching crushes:", error);
    }
  }, [token]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crush/config`);
      setConfig(res.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchCrossings(), fetchCrushes(), fetchConfig()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchCrossings, fetchCrushes, fetchConfig]);

  const toggleVisibility = async () => {
    try {
      const newVisibility = !isVisible;
      await axios.put(`${API}/crush/visibility`, 
        { is_visible: newVisibility },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setIsVisible(newVisibility);
      toast.success(newVisibility ? "Mode visible activé" : "Mode invisible activé");
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors du changement de mode");
    }
  };

  const detectCrossings = async () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non disponible");
      return;
    }

    setDetecting(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await axios.post(`${API}/crush/detect`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            detection_method: "hybrid"
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (res.data.detected > 0) {
            toast.success(res.data.message, { duration: 5000 });
            fetchCrossings();
            fetchStats();
          } else {
            toast.info(res.data.message);
          }
        } catch (error) {
          toast.error("Erreur lors de la détection");
        } finally {
          setDetecting(false);
        }
      },
      (error) => {
        toast.error("Impossible d'obtenir votre position");
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const likeCrossing = async (crossingId) => {
    try {
      const res = await axios.post(`${API}/crush/like`, 
        { crossing_id: crossingId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.status === "crush") {
        toast.success(`💕 Crush avec ${res.data.matched_with.name} !`, {
          duration: 5000
        });
        fetchCrushes();
      } else {
        toast.success("❤️ Like envoyé secrètement !");
      }
      fetchCrossings();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors du like");
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
  };

  const getPhotoUrl = (path) => {
    if (path && token) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-pink-500" size={48} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            <Sparkles className="inline mr-2 text-pink-500" size={28} />
            Croisements
          </h1>
          <p className="text-slate-500 mt-1">Réputation par appréciation mutuelle</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="guardian-card text-center">
              <CardContent className="p-4">
                <Users className="mx-auto mb-2 text-blue-500" size={24} />
                <p className="text-2xl font-extrabold">{stats.total_crossings}</p>
                <p className="text-xs text-slate-500">Croisements</p>
              </CardContent>
            </Card>
            <Card className="guardian-card text-center bg-gradient-to-br from-pink-50 to-red-50">
              <CardContent className="p-4">
                <Heart className="mx-auto mb-2 text-pink-500" size={24} />
                <p className="text-2xl font-extrabold text-pink-600">{stats.total_crushes}</p>
                <p className="text-xs text-slate-500">Crushes</p>
              </CardContent>
            </Card>
            <Card className="guardian-card text-center">
              <CardContent className="p-4">
                <Sparkles className="mx-auto mb-2 text-amber-500" size={24} />
                <p className="text-2xl font-extrabold">{stats.crushes_this_week}</p>
                <p className="text-xs text-slate-500">Cette semaine</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Visibility Toggle */}
        <Card className="guardian-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isVisible ? (
                  <Eye className="text-green-500" size={24} />
                ) : (
                  <EyeOff className="text-slate-400" size={24} />
                )}
                <div>
                  <p className="font-bold">Mode {isVisible ? "Visible" : "Invisible"}</p>
                  <p className="text-xs text-slate-500">
                    {isVisible 
                      ? "Vos croisements sont détectés" 
                      : "Aucun croisement n'est enregistré"}
                  </p>
                </div>
              </div>
              <Switch
                checked={isVisible}
                onCheckedChange={toggleVisibility}
                className="data-[state=checked]:bg-green-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* Detect Button */}
        <Button
          onClick={detectCrossings}
          disabled={detecting || !isVisible}
          className="w-full btn-guardian rounded-full py-6 text-lg"
          data-testid="detect-crossings-btn"
        >
          {detecting ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={20} />
              Détection en cours...
            </>
          ) : (
            <>
              <Radio className="mr-2" size={20} />
              Détecter les croisements
            </>
          )}
        </Button>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="text-blue-500 mt-0.5" size={20} />
              <div className="text-sm text-blue-800">
                <p className="font-bold mb-1">Comment ça marche ?</p>
                <ul className="space-y-1 text-blue-700">
                  <li>• <strong>Même lieu, même moment</strong> : croisez des personnes à moins de {config?.radius_meters || 250}m au même instant</li>
                  <li>• <strong>Délai de sécurité</strong> : les croisements s'affichent après ~{config?.display_delay_minutes || 30} min (anti-traque)</li>
                  <li>• Likez anonymement les croisements positifs</li>
                  <li>• Si l'autre vous like aussi = <strong>Crush !</strong> (identités révélées)</li>
                  <li>• <strong>Pas de notation négative</strong> possible</li>
                  <li>• Historique conservé {config?.expiry_days || 14} jours</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="crossings">
          <TabsList className="rounded-full bg-slate-100 p-1 w-full">
            <TabsTrigger value="crossings" className="flex-1 rounded-full data-[state=active]:bg-white font-bold">
              <Users size={16} className="mr-2" />
              Croisements ({crossings.length})
            </TabsTrigger>
            <TabsTrigger value="crushes" className="flex-1 rounded-full data-[state=active]:bg-white font-bold">
              <Heart size={16} className="mr-2" />
              Crushes ({crushes.length})
            </TabsTrigger>
          </TabsList>

          {/* Crossings Tab */}
          <TabsContent value="crossings" className="mt-4">
            {crossings.length === 0 ? (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center">
                  <Users size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Aucun croisement récent</p>
                  <p className="text-xs text-slate-400 mt-2">
                    Activez la détection pour croiser des personnes à proximité
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {crossings.map((crossing) => (
                  <Card 
                    key={crossing.id} 
                    className={`guardian-card transition-all ${
                      crossing.is_crush ? 'bg-gradient-to-r from-pink-50 to-red-50 border-pink-200' : ''
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            crossing.is_crush 
                              ? 'bg-gradient-to-br from-pink-400 to-red-400' 
                              : 'bg-slate-200'
                          }`}>
                            {crossing.is_crush ? (
                              <Heart className="text-white" size={24} />
                            ) : (
                              <Lock className="text-slate-400" size={20} />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-sm">
                              {crossing.is_crush ? "Crush mutuel !" : "Croisement anonyme"}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Clock size={12} />
                              <span>{crossing.time_ago || crossing.crossed_at}</span>
                              {crossing.city && (
                                <>
                                  <MapPin size={12} />
                                  <span>{crossing.city}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              {crossing.detection_method === "hybrid" && (
                                <Badge variant="outline" className="text-xs py-0">
                                  <Bluetooth size={10} className="mr-1" />
                                  Hybride
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {!crossing.is_crush && !crossing.has_liked && (
                          <Button
                            onClick={() => likeCrossing(crossing.id)}
                            className="rounded-full bg-pink-500 hover:bg-pink-600"
                            size="sm"
                          >
                            <Heart size={16} className="mr-1" />
                            Like
                          </Button>
                        )}
                        
                        {crossing.has_liked && !crossing.is_crush && (
                          <Badge className="bg-pink-100 text-pink-600">
                            <Heart size={12} className="mr-1" fill="currentColor" />
                            Liké
                          </Badge>
                        )}
                        
                        {crossing.is_crush && (
                          <Badge className="bg-gradient-to-r from-pink-500 to-red-500 text-white">
                            <Sparkles size={12} className="mr-1" />
                            Crush !
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Crushes Tab */}
          <TabsContent value="crushes" className="mt-4">
            {crushes.length === 0 ? (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center">
                  <Heart size={48} className="mx-auto mb-4 text-pink-200" />
                  <p className="text-slate-500">Aucun crush pour le moment</p>
                  <p className="text-xs text-slate-400 mt-2">
                    Likez vos croisements, si c'est réciproque ce sera un Crush !
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {crushes.map((crush) => (
                  <Card 
                    key={crush.id} 
                    className="guardian-card bg-gradient-to-r from-pink-50 to-red-50 border-pink-200"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-14 w-14 ring-2 ring-pink-300">
                            <AvatarImage src={getPhotoUrl(crush.user_photo)} />
                            <AvatarFallback className="bg-gradient-to-br from-pink-400 to-red-400 text-white font-bold">
                              {getInitials(crush.user_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold">{crush.user_name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Badge variant="outline" className="text-xs py-0">
                                {crush.user_level}
                              </Badge>
                              <span>•</span>
                              <Heart size={10} className="text-pink-500" />
                              <span>{crush.crush_count} crushes</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                              <Sparkles size={10} />
                              <span>Match le {new Date(crush.matched_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="text-slate-300" size={20} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Security Info */}
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="text-green-500 mt-0.5" size={20} />
              <div className="text-sm text-green-800">
                <p className="font-bold mb-1">Votre vie privée est protégée</p>
                <ul className="space-y-1 text-green-700 text-xs">
                  <li>• <strong>Croisements anonymisés</strong> (IDs hachés + chiffrés)</li>
                  <li>• <strong>Position exacte jamais partagée</strong> (ville seulement)</li>
                  <li>• <strong>Délai anti-traque</strong> de {config?.display_delay_minutes || 30} min</li>
                  <li>• Données supprimées après {config?.expiry_days || 14} jours</li>
                  <li>• Identités révélées uniquement en cas de Crush mutuel</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CrushPage;
